import { ReactNode, useMemo } from "react";
import { useFormCoreInput } from "../core/useFormCoreInput";
import { IFormInputProps } from "./FormInput";

interface IUseFormInputProps {
  label: string;
  defaultValue?: string;
  placeholder?: string;
  tooltip?: ReactNode;
  hint?: ReactNode;
  validation?: {
    required?: boolean;
    pattern?: RegExp;
    maxLength?: number;
  };
}

/** An adapter wrapped around useFormCoreInput */
export const useFormInput = ({
  label,
  defaultValue,
  placeholder,
  tooltip,
  hint,
  validation,
}: IUseFormInputProps) => {
  // --------------- STATES ---------------

  const {
    value,
    reset,
    setExternalError,
    errorText,
    wasChanged,
    renderProps: coreInputRenderProps,
  } = useFormCoreInput({
    defaultValue,
    placeholder,
    validation,
  });

  // --------------- COMPUTED ---------------

  const hasError = useMemo(() => Boolean(errorText), [errorText]);

  const renderProps = useMemo(
    () =>
      ({
        label,
        required: validation?.required,
        errorText,
        hint,
        ...coreInputRenderProps,
        tooltip,
      }) satisfies IFormInputProps,
    [
      label,
      validation?.required,
      errorText,
      hint,
      coreInputRenderProps,
      tooltip,
    ]
  );

  return {
    value,
    reset,
    setExternalError,
    hasError,
    wasChanged,
    renderProps,
  };
};
